import { useState, useEffect, useCallback } from "react";
import { API } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Card, CardContent } from "./ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { toast } from "sonner";
import {
  HardHat, ArrowLeft, Plus, Download, Trash2, Check, Clock, BookOpen,
  TrendingUp, DollarSign, Loader2, ChevronRight, UserPlus,
} from "lucide-react";

const fmtDate = (d) => (d ? new Date(d + (d.length === 10 ? "T00:00:00" : "")).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "");

const ProgressBar = ({ percent, tone = "navy" }) => {
  const bg = tone === "gold" ? "bg-brand-gold" : tone === "emerald" ? "bg-emerald-500" : "bg-brand-navy";
  return (
    <div className="w-full h-2.5 rounded-full bg-slate-200 overflow-hidden">
      <div className={`h-full ${bg} transition-all`} style={{ width: `${Math.min(100, percent || 0)}%` }} />
    </div>
  );
};

// ── Detail view ────────────────────────────────────────────────────────────
const ApprenticeshipDetail = ({ userId, onBack }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hours, setHours] = useState({ type: "ojl", hours: "", date: new Date().toISOString().slice(0, 10), note: "" });
  const [savingHours, setSavingHours] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/apprenticeships/${userId}`, { credentials: "include" });
      if (r.ok) setData(await r.json());
    } finally { setLoading(false); }
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  const addHours = async () => {
    if (!hours.hours || Number(hours.hours) <= 0) { toast.error("Enter a number of hours greater than zero"); return; }
    setSavingHours(true);
    try {
      const r = await fetch(`${API}/admin/apprenticeships/${userId}/hours`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ ...hours, hours: Number(hours.hours) }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || "Failed");
      setData(await r.json());
      setHours((h) => ({ ...h, hours: "", note: "" }));
      toast.success("Hours logged");
    } catch (e) { toast.error(e.message); } finally { setSavingHours(false); }
  };

  const deleteHours = async (entryId) => {
    const r = await fetch(`${API}/admin/apprenticeships/${userId}/hours/${entryId}`, { method: "DELETE", credentials: "include" });
    if (r.ok) { setData(await r.json()); toast.success("Entry removed"); } else toast.error("Failed to remove");
  };

  const toggleComp = async (comp) => {
    const r = await fetch(`${API}/admin/apprenticeships/${userId}/competency/${comp.id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
      body: JSON.stringify({ completed: !comp.completed }),
    });
    if (r.ok) setData(await r.json()); else toast.error("Failed to update competency");
  };

  const downloadPdf = async () => {
    const r = await fetch(`${API}/admin/apprenticeships/${userId}/compliance.pdf`, { credentials: "include" });
    if (!r.ok) { toast.error("Could not generate PDF"); return; }
    const blob = await r.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `apprenticeship-${userId}.pdf`;
    document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    toast.success("Compliance PDF downloaded");
  };

  if (loading) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  if (!data) return <div className="py-8 text-slate-500">Could not load apprenticeship.</div>;

  const w = data.wage;
  return (
    <div className="space-y-6" data-testid="apprenticeship-detail">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button onClick={onBack} className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-brand-navy" data-testid="appr-back-btn">
          <ArrowLeft className="w-4 h-4" /> All apprentices
        </button>
        <Button onClick={downloadPdf} className="btn-primary gap-2" data-testid="appr-pdf-btn">
          <Download className="w-4 h-4" /> Export Compliance PDF
        </Button>
      </div>

      <div>
        <h2 className="text-2xl font-heading font-bold text-slate-900">{data.apprentice_name}</h2>
        <p className="text-sm text-slate-500">{data.apprentice_email} · Cybersecurity Apprenticeship · Started {fmtDate(data.start_date)}</p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="card-surface" data-testid="appr-ojl-card">
          <CardContent className="pt-5 pb-5 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-[11px] uppercase tracking-wider font-bold text-slate-500">OJL Hours</p>
              <TrendingUp className="w-4 h-4 text-brand-navy" />
            </div>
            <p className="text-2xl font-heading font-bold text-slate-900 tabular-nums">{data.ojl_hours_logged}<span className="text-base text-slate-400"> / {data.ojl_target}</span></p>
            <ProgressBar percent={data.ojl_percent} tone="navy" />
            <p className="text-xs text-slate-500">{data.ojl_percent}% toward 2,000-hour requirement</p>
          </CardContent>
        </Card>
        <Card className="card-surface" data-testid="appr-wage-card">
          <CardContent className="pt-5 pb-5 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Current Wage</p>
              <DollarSign className="w-4 h-4 text-brand-gold" />
            </div>
            <p className="text-2xl font-heading font-bold text-slate-900 tabular-nums">${w.current_rate.toFixed(2)}<span className="text-base text-slate-400">/hr</span></p>
            <Badge className="bg-brand-navy/10 text-brand-navy border-brand-navy/20">{w.current_step_label}</Badge>
            {w.next_step_label && <p className="text-xs text-slate-500">{w.hours_to_next_step} hrs to {w.next_step_label}</p>}
            {w.end_wage_pending_competencies && <p className="text-xs text-amber-700">End wage ${w.end_wage_rate.toFixed(2)} unlocks once all 48 competencies are signed off.</p>}
          </CardContent>
        </Card>
        <Card className="card-surface" data-testid="appr-comp-card">
          <CardContent className="pt-5 pb-5 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Competencies</p>
              <BookOpen className="w-4 h-4 text-emerald-600" />
            </div>
            <p className="text-2xl font-heading font-bold text-slate-900 tabular-nums">{data.competencies_completed}<span className="text-base text-slate-400"> / {data.competencies_total}</span></p>
            <ProgressBar percent={data.competencies_percent} tone="emerald" />
            <p className="text-xs text-slate-500">RTI hours logged: {data.rti_hours_logged} (target {data.rti_target_per_year}/yr)</p>
          </CardContent>
        </Card>
      </div>

      {/* Wage ladder */}
      <Card className="card-surface">
        <CardContent className="pt-5 pb-5">
          <h3 className="font-heading font-bold text-slate-900 mb-3">Wage Progression Ladder</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {data.wage_ladder.map((s) => {
              const active = s.step === w.current_step;
              return (
                <div key={s.step} className={`rounded-xl border-2 p-3 ${active ? "border-brand-gold bg-brand-gold/10" : "border-slate-200 bg-white"}`} data-testid={`wage-step-${s.step}`}>
                  <p className="text-xs font-bold text-slate-500">{s.label}</p>
                  <p className="text-lg font-heading font-bold text-slate-900">${s.rate.toFixed(2)}/hr</p>
                  <p className="text-xs text-slate-400">{s.min_hours.toLocaleString()}–{s.max_hours.toLocaleString()} hrs</p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Hours log */}
      <Card className="card-surface">
        <CardContent className="pt-5 pb-5 space-y-4">
          <h3 className="font-heading font-bold text-slate-900 flex items-center gap-2"><Clock className="w-4 h-4" /> Log Hours</h3>
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 items-end">
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Type</Label>
              <Select value={hours.type} onValueChange={(v) => setHours((h) => ({ ...h, type: v }))}>
                <SelectTrigger className="input-base" data-testid="hours-type"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="ojl">OJL (on-the-job)</SelectItem>
                  <SelectItem value="rti">RTI (instruction)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Hours</Label>
              <Input type="number" step="0.25" className="input-base" value={hours.hours} onChange={(e) => setHours((h) => ({ ...h, hours: e.target.value }))} placeholder="e.g. 40" data-testid="hours-amount" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Date</Label>
              <Input type="date" className="input-base" value={hours.date} onChange={(e) => setHours((h) => ({ ...h, date: e.target.value }))} data-testid="hours-date" />
            </div>
            <div className="space-y-1 sm:col-span-1">
              <Label className="text-xs text-slate-500">Note</Label>
              <Input className="input-base" value={hours.note} onChange={(e) => setHours((h) => ({ ...h, note: e.target.value }))} placeholder="Optional" data-testid="hours-note" />
            </div>
            <Button className="btn-primary gap-1" onClick={addHours} disabled={savingHours || !hours.hours || Number(hours.hours) <= 0} data-testid="hours-add-btn">
              {savingHours ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add
            </Button>
          </div>
          {data.hours_log.length === 0 ? (
            <p className="text-sm text-slate-400">No hours logged yet.</p>
          ) : (
            <div className="divide-y divide-slate-100">
              {data.hours_log.map((h) => (
                <div key={h.entry_id} className="py-2 flex items-center justify-between gap-3 text-sm" data-testid={`hours-row-${h.entry_id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <Badge className={h.type === "ojl" ? "bg-brand-navy/10 text-brand-navy border-brand-navy/20" : "bg-amber-100 text-amber-700 border-amber-200"}>{h.type.toUpperCase()}</Badge>
                    <span className="font-semibold text-slate-800 tabular-nums">{h.hours} hrs</span>
                    <span className="text-slate-400">{fmtDate(h.date)}</span>
                    {h.note && <span className="text-slate-500 truncate">· {h.note}</span>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs text-slate-400">{h.logged_by_name}</span>
                    <button onClick={() => deleteHours(h.entry_id)} className="p-1 rounded hover:bg-rose-50 text-rose-600" data-testid={`hours-delete-${h.entry_id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Competency checklist */}
      <Card className="card-surface">
        <CardContent className="pt-5 pb-5 space-y-4">
          <h3 className="font-heading font-bold text-slate-900">Competency Checklist <span className="text-sm font-normal text-slate-500">· 16 areas / 48 competencies</span></h3>
          <div className="space-y-5">
            {data.areas.map((area) => {
              const done = area.items.filter((i) => i.completed).length;
              return (
                <div key={area.area} data-testid={`comp-area-${area.area}`}>
                  <div className="flex items-center justify-between mb-1">
                    <p className="font-semibold text-slate-800 text-sm">{area.area}. {area.title}</p>
                    <span className="text-xs text-slate-400">{done}/{area.items.length}</span>
                  </div>
                  <p className="text-xs text-slate-400 italic mb-2">Soft skills: {area.soft_skills}</p>
                  <div className="space-y-1.5">
                    {area.items.map((it) => (
                      <button
                        key={it.id}
                        onClick={() => toggleComp(it)}
                        className={`w-full text-left flex items-start gap-2.5 rounded-lg border px-3 py-2 transition ${it.completed ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-white hover:border-brand-navy/30"}`}
                        data-testid={`comp-item-${it.id}`}
                      >
                        <span className={`mt-0.5 w-4 h-4 rounded flex items-center justify-center shrink-0 ${it.completed ? "bg-emerald-600 text-white" : "border border-slate-300"}`}>
                          {it.completed && <Check className="w-3 h-3" />}
                        </span>
                        <span className="min-w-0">
                          <span className="text-sm text-slate-800">{it.name}</span>
                          {it.completed && it.signed_off_by_name && (
                            <span className="block text-xs text-emerald-700">Signed off by {it.signed_off_by_name} · {fmtDate((it.signed_off_at || "").slice(0, 10))}</span>
                          )}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ── List + create view ───────────────────────────────────────────────────────
const ApprenticeshipTab = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [newUserId, setNewUserId] = useState("");
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/apprenticeships`, { credentials: "include" });
      if (r.ok) setList(await r.json());
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openAdd = async () => {
    setAddOpen(true);
    try {
      const r = await fetch(`${API}/users`, { credentials: "include" });
      if (r.ok) {
        const us = await r.json();
        const existing = new Set(list.map((a) => a.user_id));
        setParticipants(us.filter((u) => u.role === "participant" && u.user_id && !existing.has(u.user_id)));
      }
    } catch { /* ignore */ }
  };

  const create = async () => {
    if (!newUserId) { toast.error("Select a participant"); return; }
    setCreating(true);
    try {
      const r = await fetch(`${API}/admin/apprenticeships`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ user_id: newUserId, start_date: startDate }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || "Failed");
      toast.success("Apprentice enrolled");
      setAddOpen(false); setNewUserId("");
      load();
    } catch (e) { toast.error(e.message); } finally { setCreating(false); }
  };

  if (selected) return <ApprenticeshipDetail userId={selected} onBack={() => { setSelected(null); load(); }} />;

  return (
    <div className="space-y-5" data-testid="apprenticeship-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <HardHat className="w-5 h-5 text-brand-navy" />
          <h2 className="text-xl font-heading font-bold text-slate-900">Apprenticeship Compliance</h2>
          <Badge className="bg-slate-100 text-slate-600 border-slate-200">{list.length}</Badge>
        </div>
        <Button className="btn-primary gap-2" onClick={openAdd} data-testid="add-apprentice-btn">
          <UserPlus className="w-4 h-4" /> Enroll apprentice
        </Button>
      </div>
      <p className="text-sm text-slate-500">Cybersecurity apprenticeship: OJL/RTI hour tracking, 48-competency checklist, and the 4-step wage ladder. Hours are entered manually (Rippling HRIS sync coming soon).</p>

      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : list.length === 0 ? (
        <Card className="card-surface"><CardContent className="py-12 text-center text-slate-500" data-testid="appr-empty">No apprentices enrolled yet. Click "Enroll apprentice" to start tracking compliance.</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {list.map((a) => (
            <button key={a.apprenticeship_id} onClick={() => setSelected(a.user_id)} className="text-left" data-testid={`appr-row-${a.user_id}`}>
              <Card className="card-surface hover:border-brand-navy/40 transition">
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-heading font-bold text-slate-900 truncate">{a.apprentice_name}</p>
                      <p className="text-xs text-slate-500 truncate">{a.apprentice_email}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <p className="text-slate-500 mb-1">OJL {a.ojl_hours_logged}/2000 ({a.ojl_percent}%)</p>
                      <ProgressBar percent={a.ojl_percent} tone="navy" />
                    </div>
                    <div>
                      <p className="text-slate-500 mb-1">Competencies {a.competencies_completed}/{a.competencies_total} ({a.competencies_percent}%)</p>
                      <ProgressBar percent={a.competencies_percent} tone="emerald" />
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <Badge className="bg-brand-gold/20 text-brand-navy border-brand-gold/40">{a.current_step_label}</Badge>
                    <span className="text-sm font-bold text-slate-700">${a.current_rate.toFixed(2)}/hr</span>
                  </div>
                </CardContent>
              </Card>
            </button>
          ))}
        </div>
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-white border-slate-200" data-testid="add-apprentice-dialog">
          <DialogHeader><DialogTitle className="text-slate-900">Enroll an apprentice</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Select a participant and start date to enroll them in the cybersecurity apprenticeship.</DialogDescription>
          <div className="space-y-4">
            <div className="space-y-1">
              <Label className="text-slate-600">Participant</Label>
              <Select value={newUserId} onValueChange={setNewUserId}>
                <SelectTrigger className="input-base" data-testid="apprentice-participant-select"><SelectValue placeholder="Select a participant" /></SelectTrigger>
                <SelectContent className="bg-white max-h-72">
                  {participants.map((p) => (
                    <SelectItem key={p.user_id} value={p.user_id}>{p.name} — {p.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {participants.length === 0 && <p className="text-xs text-slate-400">No eligible participants found.</p>}
            </div>
            <div className="space-y-1">
              <Label className="text-slate-600">Start date</Label>
              <Input type="date" className="input-base" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="apprentice-start-date" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" className="btn-outline" onClick={() => setAddOpen(false)}>Cancel</Button>
              <Button className="btn-primary gap-2" onClick={create} disabled={creating} data-testid="apprentice-create-submit">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Enroll
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ApprenticeshipTab;
